import { useMemo, useState } from "react";
import styled from "styled-components";
import { projects } from "../../data/constants";
import {
  Section,
  Eyebrow,
  SectionTitle,
  SectionIntro,
  Head,
  Tag,
  TagGhost,
  Glass,
  Reveal,
  useReveal,
} from "../../studio/ui";
import { CrossMotif } from "../../studio/SacredGeometry";
import { FiGithub, FiExternalLink, FiArrowRight } from "react-icons/fi";

/* ============================================================
   PROJECTS — Divine Futurism. Frosted stained-glass relics
   beneath an embossed sacred cross, lit by gold & violet aura.
============================================================ */

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 40px;

  @media ${({ theme }) => theme.bp.md} {
    margin-bottom: 28px;
  }
`;

const Filter = styled.button`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 12.5px;
  letter-spacing: 0.06em;
  text-transform: capitalize;
  padding: 9px 18px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.28s ease;
  backdrop-filter: blur(8px);

  border: 1px solid
    ${({ theme, $on }) => ($on ? "transparent" : theme.border)};
  background: ${({ theme, $on }) =>
    $on
      ? `linear-gradient(100deg, ${theme.goldSoft}, ${theme.gold})`
      : theme.glass};
  color: ${({ theme, $on }) => ($on ? "#0b0a14" : theme.textMuted)};
  box-shadow: ${({ theme, $on }) =>
    $on ? `0 0 24px -6px ${theme.goldGlow}` : "none"};
  font-weight: ${({ $on }) => ($on ? 600 : 500)};

  &:hover {
    color: ${({ theme, $on }) => ($on ? "#0b0a14" : theme.gold)};
    border-color: ${({ theme, $on }) =>
      $on ? "transparent" : theme.borderHi};
    box-shadow: 0 0 22px -8px ${({ theme }) => theme.goldGlow};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 22px;
`;

const Card = styled(Glass)`
  display: flex;
  flex-direction: column;
  cursor: pointer;

  &:hover .thumb img {
    transform: scale(1.06);
  }
`;

const Thumb = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: ${({ theme }) => theme.veil};
  border-bottom: 1px solid ${({ theme }) => theme.border};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  }

  /* gold-tinted vignette / halo */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
        120% 90% at 50% 0%,
        transparent 40%,
        rgba(201, 168, 76, 0.1)
      ),
      linear-gradient(
        180deg,
        rgba(6, 6, 11, 0) 45%,
        rgba(6, 6, 11, 0.72)
      );
    pointer-events: none;
  }
`;

const CatPill = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.goldSoft};
  background: rgba(11, 10, 24, 0.66);
  border: 1px solid ${({ theme }) => theme.borderHi};
  padding: 5px 11px;
  border-radius: 999px;
  backdrop-filter: blur(6px);
`;

const Body = styled.div`
  position: relative;
  z-index: 1;
  padding: 22px;
  display: flex;
  flex-direction: column;
  flex: 1;

  h3 {
    font-family: ${({ theme }) => theme.fonts.serif};
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.white};
  }
  .tagline {
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 12px;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.gold};
    margin-top: 5px;
  }
  p {
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 14px;
    line-height: 1.65;
    color: ${({ theme }) => theme.textMuted};
    margin: 13px 0 16px;
  }
`;

/* embossed cross motif beneath the frosted body */
const Emboss = styled.div`
  position: absolute;
  bottom: -22px;
  right: -18px;
  z-index: 0;
  opacity: 0.5;
  pointer-events: none;
  filter: drop-shadow(0 0 6px ${({ theme }) => theme.goldGlow});
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 18px;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: auto;
  padding-top: 15px;
  border-top: 1px solid ${({ theme }) => theme.border};

  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 13.5px;
    font-weight: 600;
    color: ${({ theme }) => theme.textMuted};
    transition: color 0.25s ease;
    &:hover {
      color: ${({ theme }) => theme.gold};
    }
  }
  .more {
    margin-left: auto;
    color: ${({ theme }) => theme.goldSoft};
  }
`;

function ProjectCard({ project, i, onOpen }) {
  const [ref, shown] = useReveal(0.1);
  const desc =
    project.description.length > 130
      ? project.description.slice(0, 130).trim() + "…"
      : project.description;

  return (
    <Reveal
      ref={ref}
      $in={shown}
      $dir={i % 2 === 0 ? "left" : "right"}
      $delay={(i % 3) * 0.05}
    >
      <Card onClick={() => onOpen(project)}>
        <Thumb className="thumb">
          {project.image && <img src={project.image} alt={project.title} />}
          <CatPill>{project.category}</CatPill>
        </Thumb>
        <Body>
          <Emboss>
            <CrossMotif size={120} />
          </Emboss>
          <h3>{project.title}</h3>
          <div className="tagline">{project.tagline}</div>
          <p>{desc}</p>
          <Tags>
            {project.tags.slice(0, 4).map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
            {project.tags.length > 4 && (
              <TagGhost>+{project.tags.length - 4}</TagGhost>
            )}
          </Tags>
          <Foot onClick={(e) => e.stopPropagation()}>
            {project.github && (
              <a href={project.github} target="_blank" rel="noreferrer">
                <FiGithub /> Code
              </a>
            )}
            {project.webapp && (
              <a href={project.webapp} target="_blank" rel="noreferrer">
                <FiExternalLink /> Live
              </a>
            )}
            <a
              className="more"
              href="#projects"
              onClick={(e) => {
                e.preventDefault();
                onOpen(project);
              }}
            >
              Details <FiArrowRight />
            </a>
          </Foot>
        </Body>
      </Card>
    </Reveal>
  );
}

export default function Projects({ setOpenModal }) {
  const categories = useMemo(
    () => ["all", ...Array.from(new Set(projects.map((p) => p.category)))],
    []
  );
  const [active, setActive] = useState("all");

  const list = useMemo(
    () =>
      active === "all"
        ? projects
        : projects.filter((p) => p.category === active),
    [active]
  );

  return (
    <Section id="projects">
      <Head>
        <Eyebrow>Projects</Eyebrow>
        <SectionTitle>
          Works &amp; <em>relics</em>
        </SectionTitle>
        <SectionIntro>
          A selection of full-stack, infrastructure, and blockchain builds.
          Open any card for the full breakdown.
        </SectionIntro>
      </Head>

      <Filters>
        {categories.map((c) => (
          <Filter key={c} $on={active === c} onClick={() => setActive(c)}>
            {c}
          </Filter>
        ))}
      </Filters>

      <Grid>
        {list.map((p, i) => (
          <ProjectCard
            key={p.title}
            project={p}
            i={i}
            onOpen={(proj) => setOpenModal({ state: true, project: proj })}
          />
        ))}
      </Grid>
    </Section>
  );
}
